import { COBarChartViewProps } from 'enums/COBarChartEums';
import React, { useMemo, useRef, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Label,
  TooltipProps,
} from 'recharts';
import { CategoryOptions } from '..';
import LoaderAnimation from '../../../shared/components/LoaderAnimation';
import styles from './COBarChart.module.scss';
import GraphDropDown, { GraphDropDownProps } from './GraphDropdown';
import { Spin } from 'antd';
import { DownloadLoader } from 'enums/GraphDropdownEnums';
//import { truncateWithEllipsis } from 'shared/utils/truncateWithElipsis';
import CustomTooltip from 'shared/components/CustomisedTooltip';
import { getNiceYAxisScale } from 'shared/utils/chartScale';
import { ProductDashboardChartsViewProps } from 'enums/ProductDashboardChartsEnums';
interface COBarChartProps {
  data?: CategoryOptions[];
  heading?: boolean;
  eachStage?: boolean;
  loading: boolean;
  graphDropDownProps?: GraphDropDownProps;
  isSort?: boolean;
  selectedOption?: string;
  graphName?: string;
}

export const renderTooltipContent = (
  tooltipProps: TooltipProps<string, string>
) => {
  const { payload } = tooltipProps;
  return payload && payload.length ? (
    <CustomTooltip
      isBar
      payload={payload[0]?.payload}
      unit={ProductDashboardChartsViewProps.KGCO2}
    />
  ) : null;
};

const COBarChart: React.FC<COBarChartProps> = ({
  data,
  heading,
  eachStage,
  loading,
  graphDropDownProps,
  isSort,
  selectedOption,
  graphName,
}: COBarChartProps) => {
  const graphRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const yAxisScale = useMemo(() => {
    const values = (data ?? []).map(
      (row) => (row as { emission?: number }).emission as number
    );
    return getNiceYAxisScale(values, { tickCount: 4 });
  }, [data]);

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <LoaderAnimation />
      </div>
    );
  }

  const customPadding = {
    left: data?.length === 1 ? 100 : data?.length === 2 ? 200 : 0,
    right: data?.length === 1 ? 100 : data?.length === 2 ? 200 : 0,
  };

  //const getTruncatedLabel = (value: string) => truncateWithEllipsis(value);

  return (
    <Spin spinning={!!isDownloading} tip={DownloadLoader.DOWNLOADING}>
      {graphDropDownProps && (
        <GraphDropDown
          handleDownload={graphDropDownProps.handleDownload}
          handleSort={graphDropDownProps.handleSort}
          graphRef={graphRef}
          downloadCsv={graphDropDownProps.downloadCsv}
          isSort={isSort}
          selectedOption={selectedOption}
          downloadFileName={graphName}
          setIsDownloading={setIsDownloading}
        />
      )}
      <div ref={graphRef}>
        <ResponsiveContainer width={COBarChartViewProps.RES_WIDTH} height={320}>
          <BarChart
            data={data}
            margin={{
              top: 40,
              right: 29,
              bottom: 30,
              left: 40,
            }}>
            <CartesianGrid
              strokeDasharray="5 3"
              horizontal={true}
              vertical={false}
            />
            {heading ? (
              <XAxis
                dataKey="name"
                strokeDasharray="5 5"
                angle={-15}
                dy={10}
                dx={-39}
                className={styles.xAxisLabel}
              />
            ) : (
              <XAxis
                dataKey="name"
                strokeDasharray="5 5"
                angle={-15}
                dy={10}
                dx={-39}
                className={styles.xAxisLabel}
                padding={customPadding}
                //tickFormatter={getTruncatedLabel}
              />
            )}
            {heading && eachStage ? (
              <YAxis
                domain={yAxisScale.domain}
                ticks={yAxisScale.ticks}
                allowDataOverflow
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              >
                <Label
                  value={COBarChartViewProps.LABEL_VALUE2}
                  position={COBarChartViewProps.LABEL_POSITION}
                  angle={-90}
                  offset={-10}
                  style={{
                    textAnchor: COBarChartViewProps.TEXT_ANCHOR,
                    fill: COBarChartViewProps.TEXT_ANCHOR_FILL,
                  }}
                  className={styles.labels}
                />
              </YAxis>
            ) : (
              <YAxis
                domain={yAxisScale.domain}
                ticks={yAxisScale.ticks}
                allowDataOverflow
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              >
                <Label
                  value={COBarChartViewProps.LABEL_VALUE}
                  position={COBarChartViewProps.LABEL_POSITION}
                  angle={-90}
                  offset={-10}
                  style={{
                    textAnchor: COBarChartViewProps.TEXT_ANCHOR,
                    fill: COBarChartViewProps.TEXT_ANCHOR_FILL,
                  }}
                  className={styles.labels}
                />
              </YAxis>
            )}
            <Tooltip content={renderTooltipContent} />
            <Bar
              dataKey={COBarChartViewProps.BAR_DATAKEY}
              fill={COBarChartViewProps.BAR_FILL}
              radius={[5, 5, 0, 0]}
              barSize={35}>
              <LabelList
                dataKey={COBarChartViewProps.LABEL_LIST_DATAKEY}
                position={COBarChartViewProps.LABEL_LIST_POSITION}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Spin>
  );
};

export default COBarChart;
